/** 离线画册只带这次允许读到的文字和 512 档图片；服务端标识留在取图计划里，不写进文件。 */
import type { SnapshotResponse } from '@star-sea/shared';
import { phaseOf } from './capsule.ts';

export const ALBUM_MAX_BYTES = 150 * 1024 * 1024;

export interface AlbumContent {
  kind: 'photo' | 'capsule';
  date: string;
  title: string;
  story?: string;
  place?: string;
  caption?: string;
  faces: { side: 'A' | 'B'; name: string; text: string }[];
  letter?: string;
}

export interface AlbumItem {
  photoId?: string;
  thumbs?: boolean;
  content: AlbumContent;
}

export interface AlbumPlan {
  name: string;
  since: string;
  items: AlbumItem[];
  years: number[];
  photoCount: number;
}

export interface AlbumPart {
  name: string;
  blob: Blob;
  count: number;
  photos: number;
  years: number[];
  index: number;
}

export interface AlbumProgress {
  completed: number;
  total: number;
  /** 已取回的图片字节；分卷另外按完整 HTML 的实际字节算。 */
  bytes: number;
}

export interface AlbumOptions {
  signal?: AbortSignal;
  maxBytes?: number;
  concurrency?: number;
  onProgress?: (progress: AlbumProgress) => void;
}

export type AlbumLoader = (photoId: string, signal: AbortSignal) => Promise<Blob>;

/** 普通照片保留日期、回忆和可见视角；胶囊只有亲手拆过后才导出，偷看不算。 */
export function prepareAlbum(snapshot: SnapshotResponse, viewerId: string, year?: number): AlbumPlan {
  const memories = new Map(snapshot.memories.map((memory) => [memory.id, memory]));
  const names = new Map(snapshot.members.map((member) => [member.id, member.displayName]));
  const perspectives = new Map<string, NonNullable<SnapshotResponse['perspectives']>>();
  for (const perspective of snapshot.perspectives ?? []) {
    const list = perspectives.get(perspective.photoId) ?? [];
    list.push(perspective);
    perspectives.set(perspective.photoId, list);
  }
  const selected = (date: string) => year === undefined || Number(date.slice(0, 4)) === year;
  const items: AlbumItem[] = [];
  for (const photo of snapshot.photos) {
    if (!selected(photo.date)) continue;
    const memory = memories.get(photo.memoryId);
    const list = perspectives.get(photo.id) ?? [];
    const wrote = list.some((p) => p.memberId === viewerId && !p.hidden && p.text !== undefined);
    const faces = list
      .filter((p) => !p.hidden && p.text !== undefined && names.has(p.memberId)
        && (!snapshot.space.blindPerspectives || wrote || p.memberId === viewerId))
      .map((p): AlbumContent['faces'][number] => ({
        side: p.memberId === photo.createdBy ? 'A' : 'B',
        name: names.get(p.memberId)!,
        text: p.text!,
      }))
      .sort((a, b) => a.side.localeCompare(b.side));
    items.push({ photoId: photo.id, thumbs: photo.thumbs, content: {
      kind: 'photo', date: photo.date, title: memory?.title ?? '那一天的回忆',
      story: memory?.story, place: memory?.place?.name, caption: photo.caption, faces,
    } });
  }
  for (const capsule of snapshot.capsules ?? []) {
    // 秘密惊喜的作者不用再拆一次：Ta 已拆开、服务端也给了内容，就可以留进画册。
    if (!selected(capsule.date) || phaseOf(capsule, viewerId, Date.now()) !== 'opened') continue;
    if (capsule.photo === undefined && capsule.letter === undefined) continue;
    items.push({ photoId: capsule.photo?.id, thumbs: capsule.photo?.thumbs, content: {
      kind: 'capsule', date: capsule.date, title: '已经拆开的时间胶囊',
      caption: capsule.photo?.caption, faces: [], letter: capsule.letter,
    } });
  }
  items.sort((a, b) => a.content.date.localeCompare(b.content.date));
  return {
    name: snapshot.space.name, since: snapshot.space.since, items,
    years: [...new Set(items.map((item) => Number(item.content.date.slice(0, 4))))],
    photoCount: items.filter((item) => item.photoId !== undefined).length,
  };
}

/** JSON 放在 script 文本节点里；转义 < 才能挡住用户文字中的 </script>。 */
function safeJson(value: unknown): string {
  return JSON.stringify(value).replace(/[<>&\u2028\u2029]/g, (c) => `\\u${c.charCodeAt(0).toString(16).padStart(4, '0')}`);
}

function abortError(): DOMException { return new DOMException('画册导出已取消', 'AbortError'); }
function checkAbort(signal: AbortSignal): void { if (signal.aborted) throw abortError(); }

/** 即使取图器没有及时结束，关闭导出后也要立即退出；原 Promise 仍有拒绝处理器。 */
function abortable<T>(task: Promise<T>, signal: AbortSignal): Promise<T> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) { task.catch(() => {}); reject(abortError()); return; }
    const abort = () => reject(abortError());
    signal.addEventListener('abort', abort, { once: true });
    task.then(resolve, reject).finally(() => signal.removeEventListener('abort', abort));
  });
}

function base64(bytes: Uint8Array): string {
  // 分块避免把一整张图当函数参数展开，也避免依赖 Node 的 Buffer。
  const chunks: string[] = [];
  for (let start = 0; start < bytes.length; start += 24_576) {
    chunks.push(btoa(String.fromCharCode(...bytes.subarray(start, start + 24_576))));
  }
  return chunks.join('');
}

function imageMime(bytes: Uint8Array): string | null {
  if (bytes.length >= 12 && bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46
    && bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50) return 'image/webp';
  if (bytes.length >= 8 && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47
    && bytes[4] === 0x0d && bytes[5] === 0x0a && bytes[6] === 0x1a && bytes[7] === 0x0a) return 'image/png';
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return 'image/jpeg';
  return null;
}

interface EncodedItem { json: string; bytes: number; photo: boolean; year: number }
interface YearGroup { year: number; entries: EncodedItem[]; bytes: number }
const encoder = new TextEncoder();

function fileName(name: string): string {
  return name.replace(/[\\/:*?"<>|\u0000-\u001f]/g, '').replace(/\s+/g, ' ').trim().slice(0, 60) || '星海';
}

/**
 * 先尝试装成一个文件；确实超过上限才按年拆，同一年仍太大就分卷。
 * 每次 yield 后等调用者主动 next：调用者应先保存并释放上一卷，再继续，避免堆着多个大 Blob。
 */
export async function* buildAlbum(plan: AlbumPlan, loadPhoto: AlbumLoader, options: AlbumOptions = {}): AsyncGenerator<AlbumPart> {
  const controller = new AbortController();
  const signal = controller.signal;
  const cancel = () => controller.abort();
  if (options.signal?.aborted) controller.abort();
  options.signal?.addEventListener('abort', cancel, { once: true });
  const progress: AlbumProgress = { completed: 0, total: plan.photoCount, bytes: 0 };
  let groups: YearGroup[] = [];
  try {
    checkAbort(signal);
    if (!plan.items.length) throw new Error('这个范围还没有可以导出的回忆。');
    const unavailable = plan.items.find((item) => item.photoId !== undefined && item.thumbs === false);
    if (unavailable) throw new Error(`${unavailable.content.date} 有照片尚未生成缩略图，画册无法完整导出。请先补齐显示版。`);
    const prefix = `${ALBUM_HEAD}${safeJson({ name: plan.name, since: plan.since }).slice(0, -1)},"entries":[`;
    const suffix = ALBUM_TAIL;
    const overhead = encoder.encode(prefix).length + encoder.encode(suffix).length;
    const maxBytes = options.maxBytes ?? ALBUM_MAX_BYTES;
    if (!Number.isFinite(maxBytes) || maxBytes <= overhead) throw new Error('画册分卷容量过小，装不下阅读界面。');
    const concurrency = Math.max(1, Math.min(6, Math.floor(options.concurrency ?? 3) || 1));
    let split = false, index = 0;
    const yearParts = new Map<number, number>();
    const sizeOf = (list: YearGroup[]) => overhead + list.reduce((sum, group) => sum + group.bytes, 0)
      + Math.max(0, list.reduce((sum, group) => sum + group.entries.length, 0) - 1);
    const part = (list: YearGroup[], divided: boolean): AlbumPart => {
      checkAbort(signal);
      const entries = list.flatMap((group) => group.entries);
      const chunks: BlobPart[] = [prefix];
      entries.forEach((entry, i) => { if (i) chunks.push(','); chunks.push(entry.json); });
      chunks.push(suffix);
      const year = list[0].year;
      const volume = (yearParts.get(year) ?? 0) + 1;
      yearParts.set(year, volume);
      return {
        name: `${fileName(plan.name)}-离线画册${divided ? `-${year}-${volume}` : ''}.html`,
        blob: new Blob(chunks, { type: 'text/html;charset=utf-8' }), count: entries.length,
        photos: entries.filter((entry) => entry.photo).length, years: list.map((group) => group.year), index: ++index,
      };
    };
    options.onProgress?.({ ...progress });
    for (let start = 0; start < plan.items.length; start += concurrency) {
      checkAbort(signal);
      const batch = plan.items.slice(start, start + concurrency);
      const encoded = await Promise.all(batch.map(async (item): Promise<EncodedItem> => {
        let image: string | undefined;
        if (item.photoId !== undefined) {
          try {
            const blob = await abortable(loadPhoto(item.photoId, signal), signal);
            checkAbort(signal);
            const bytes = new Uint8Array(await abortable(blob.arrayBuffer(), signal));
            checkAbort(signal);
            const mime = imageMime(bytes);
            if (!mime) throw new Error('没有收到可用的 JPEG、PNG 或 WebP 缩略图');
            image = `data:${mime};base64,${base64(bytes)}`;
            progress.completed++;
            progress.bytes += blob.size;
            options.onProgress?.({ ...progress });
          } catch (error) {
            if (signal.aborted) throw abortError();
            controller.abort();
            const detail = error instanceof Error ? error.message : '取图失败';
            throw new Error(`${item.content.date} 的照片取图失败，当前卷未导出：${detail}`);
          }
        }
        checkAbort(signal);
        // 不展开原始 DTO 或计划项，文件里绝不出现 photoId、成员 id、GPS 和访问凭据。
        const c = item.content;
        const json = safeJson({ kind: c.kind, date: c.date, title: c.title, story: c.story,
          place: c.place, caption: c.caption, faces: c.faces.map((face) => ({ side: face.side, name: face.name, text: face.text })),
          letter: c.letter, image });
        return { json, bytes: encoder.encode(json).length, photo: image !== undefined, year: Number(c.date.slice(0, 4)) };
      }));
      for (const entry of encoded) {
        checkAbort(signal);
        if (overhead + entry.bytes > maxBytes) throw new Error('一段回忆本身已超过画册分卷容量，无法生成完整文件。');
        if (!split && groups.length && sizeOf(groups) + 1 + entry.bytes > maxBytes) {
          split = true;
          while (groups.length > 1) yield part([groups.shift()!], true);
        }
        const current = groups.at(-1);
        if (split && current && (current.year !== entry.year || sizeOf(groups) + 1 + entry.bytes > maxBytes)) {
          groups = [];
          yield part([current], true);
        }
        checkAbort(signal);
        let group = groups.at(-1);
        if (!group || group.year !== entry.year) {
          group = { year: entry.year, entries: [], bytes: 0 };
          groups.push(group);
        }
        group.entries.push(entry);
        group.bytes += entry.bytes;
      }
    }
    if (groups.length) {
      const remaining = groups;
      groups = [];
      yield part(remaining, split);
    }
  } finally {
    groups = [];
    controller.abort();
    options.signal?.removeEventListener('abort', cancel);
  }
}

// 页面没有外链字体、脚本、样式或图像；CSP 再挡住意外的网络请求。
const ALBUM_HEAD = `<!doctype html>
<html lang="zh-CN"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src data:; style-src 'unsafe-inline'; script-src 'unsafe-inline'; connect-src 'none'; base-uri 'none'; form-action 'none'">
<title>星海 · 离线画册</title><style>
:root{color-scheme:dark;font-family:system-ui,-apple-system,"PingFang SC","Microsoft YaHei",sans-serif;color:#eee5d6;background:#101723}*{box-sizing:border-box}body{margin:0}button,select{font:inherit}button,select{color:inherit;background:#1b2636;border:1px solid #746d6055;border-radius:12px;padding:11px 16px}button{cursor:pointer}button:hover{border-color:#d7bc83}button:disabled{opacity:.35;cursor:default}button:focus-visible,select:focus-visible{outline:2px solid #e6cd97;outline-offset:4px}.cover,main,footer{max-width:1120px;margin:auto;padding:32px 24px}.cover{padding-top:68px}.eyebrow{color:#c4a66e;letter-spacing:.24em;font-size:12px}h1{font-family:serif;font-weight:400;font-size:clamp(30px,6vw,58px);margin:20px 0;overflow-wrap:anywhere}p{line-height:1.8}.muted{color:#a9b3c3}.tools{display:flex;gap:16px;align-items:center;flex-wrap:wrap;border-top:1px solid #ffffff14;padding-top:24px}.tools label{display:flex;gap:12px;align-items:center}.grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(190px,1fr));gap:18px}.card{padding:0;overflow:hidden;text-align:left;background:#182230;content-visibility:auto;contain-intrinsic-size:auto 260px}.card img,.letter-cover{display:block;width:100%;aspect-ratio:1;object-fit:cover;background:#1e2b3c}.letter-cover{display:grid;place-items:center;color:#d8bd85;font-size:54px}.card-copy{display:block;padding:16px}.card-date{display:block;font-size:12px;color:#b1a388;margin-bottom:8px}.card-title{display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.more{display:block;margin:28px auto 0}footer{font-size:12px;color:#99a4b5;text-align:center;padding-bottom:48px}dialog{width:min(1060px,calc(100% - 32px));max-height:calc(100dvh - 32px);padding:0;border:1px solid #dcc6a533;border-radius:18px;background:#121c29;color:inherit}dialog::backdrop{background:#050b14dd}.reader-top{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:14px 20px;position:sticky;top:0;background:#121c29;z-index:1}.reader-body{display:grid;grid-template-columns:minmax(0,1.3fr) minmax(260px,1fr);gap:30px;padding:12px 28px 28px}.reader-picture{display:flex;align-items:flex-start;justify-content:center;min-width:0}.reader-picture img{width:100%;max-height:72vh;object-fit:contain;border-radius:8px}.reader-text{min-width:0}.reader-picture[hidden]+.reader-text{grid-column:1/-1}h2{font-family:serif;font-weight:400;font-size:28px;margin:14px 0;overflow-wrap:anywhere}.prose{white-space:pre-wrap;overflow-wrap:anywhere;line-height:1.85;font-size:15px}.face{border-top:1px solid #ffffff17;margin-top:22px;padding-top:16px}.face h3{font-size:12px;letter-spacing:.08em;color:#d8bd85;font-weight:400}.reader-nav{display:flex;justify-content:space-between;gap:12px;margin-top:28px}[hidden]{display:none!important}@media(max-width:640px){.cover{padding-top:40px}.cover,main,footer{padding-left:18px;padding-right:18px}.grid{grid-template-columns:repeat(2,minmax(0,1fr));gap:12px}.card-copy{padding:12px}.card-title{font-size:14px}.reader-body{grid-template-columns:1fr;gap:22px;padding:8px 18px 24px}.reader-picture img{max-height:50vh}dialog{width:calc(100% - 16px);max-height:calc(100dvh - 16px)}.reader-top{padding:12px 16px}}@media(prefers-reduced-motion:no-preference){button{transition:border-color .15s}}
</style></head><body>
<header class="cover"><span class="eyebrow">STAR SEA · OUR MEMORIES</span><h1 id="name"></h1><p id="since" class="muted"></p><div class="tools"><label>翻到哪一年 <select id="year" aria-label="按年份筛选"></select></label><span id="count" class="muted" aria-live="polite"></span></div></header>
<main><div id="grid" class="grid"></div><button id="more" class="more" type="button">再展开一些回忆</button></main>
<footer>把星海留在身边。此画册可断网阅读；照片为 512 档浏览版。<br>文件包含私人照片和文字，请只交给你信任的人。</footer>
<dialog id="reader" aria-labelledby="reader-title"><div class="reader-top"><span id="position" class="muted"></span><button id="close" type="button" aria-label="关闭回忆">关闭 ×</button></div><article class="reader-body"><div id="picture" class="reader-picture"></div><div class="reader-text"><span id="date" class="eyebrow"></span><h2 id="reader-title"></h2><div id="details"></div><nav class="reader-nav" aria-label="翻页"><button id="previous" type="button">← 上一页</button><button id="next" type="button">下一页 →</button></nav></div></article></dialog>
<script id="album-data" type="application/json">`;

const ALBUM_TAIL = `]}</script><script>
'use strict';
const album=JSON.parse(document.getElementById('album-data').textContent);
document.getElementById('album-data').remove();
const byId=id=>document.getElementById(id);
const node=(tag,text,className)=>{const element=document.createElement(tag);if(text!==undefined)element.textContent=text;if(className)element.className=className;return element;};
document.title=album.name+' · 离线画册';byId('name').textContent=album.name;
byId('since').textContent='从 '+album.since.replaceAll('-','.')+' 开始，把每一段回忆好好收藏。';
const select=byId('year'),grid=byId('grid'),reader=byId('reader');
let filtered=[],shown=0,current=0,returnFocus=null;
const all=node('option','全部年份');all.value='';select.append(all);
for(const year of [...new Set(album.entries.map(entry=>entry.date.slice(0,4)))]){const option=node('option',year+' 年');option.value=year;select.append(option);}
function image(entry){const img=node('img');img.src=entry.image;img.alt=entry.title;return img;}
function more(){const end=Math.min(shown+60,filtered.length);for(let i=shown;i<end;i++){const entry=filtered[i],button=node('button',undefined,'card');button.type='button';button.setAttribute('aria-label',entry.date+' '+entry.title);if(entry.image){const img=image(entry);img.loading='lazy';img.decoding='async';button.append(img);}else button.append(node('span','✉','letter-cover'));const copy=node('span',undefined,'card-copy');copy.append(node('span',entry.date.replaceAll('-','.'),'card-date'),node('span',entry.title,'card-title'));button.append(copy);button.addEventListener('click',()=>{returnFocus=button;open(i);});grid.append(button);}shown=end;byId('more').hidden=shown>=filtered.length;}
function filter(){filtered=album.entries.filter(entry=>!select.value||entry.date.startsWith(select.value+'-'));shown=0;grid.replaceChildren();byId('count').textContent=filtered.length+' 段回忆 · '+filtered.filter(entry=>entry.image).length+' 张照片';more();}
function textBlock(parent,text){if(text)parent.append(node('p',text,'prose'));}
function open(index){current=index;const entry=filtered[current];byId('position').textContent=(current+1)+' / '+filtered.length;byId('date').textContent=entry.date.replaceAll('-','.');byId('reader-title').textContent=entry.title;const picture=byId('picture');picture.replaceChildren();picture.hidden=!entry.image;if(entry.image)picture.append(image(entry));const details=byId('details');details.replaceChildren();textBlock(details,entry.place);textBlock(details,entry.caption);textBlock(details,entry.story);for(const face of entry.faces){const section=node('section',undefined,'face');section.append(node('h3',face.side+' 面 · '+face.name));textBlock(section,face.text);details.append(section);}if(entry.letter!==undefined){const section=node('section',undefined,'face');section.append(node('h3','时间胶囊里的信'));textBlock(section,entry.letter);details.append(section);}byId('previous').disabled=current===0;byId('next').disabled=current===filtered.length-1;if(!reader.open)reader.showModal();reader.scrollTop=0;}
function turn(delta){const index=current+delta;if(index>=0&&index<filtered.length)open(index);}
byId('more').addEventListener('click',more);select.addEventListener('change',filter);
byId('previous').addEventListener('click',()=>turn(-1));byId('next').addEventListener('click',()=>turn(1));
byId('close').addEventListener('click',()=>reader.close());reader.addEventListener('close',()=>returnFocus?.focus());
reader.addEventListener('click',event=>{if(event.target!==reader)return;const bounds=reader.getBoundingClientRect();if(event.clientX<bounds.left||event.clientX>bounds.right||event.clientY<bounds.top||event.clientY>bounds.bottom)reader.close();});
document.addEventListener('keydown',event=>{if(!reader.open)return;if(event.key==='ArrowLeft'){event.preventDefault();turn(-1);}if(event.key==='ArrowRight'){event.preventDefault();turn(1);}if(event.key==='Escape'){event.preventDefault();reader.close();}});
filter();
</script></body></html>`;
